/**
 * ───────────────────────────────────────────────────────────────────────────
 * AUDIENCE MODEL — SINGLE SOURCE OF TRUTH
 * ───────────────────────────────────────────────────────────────────────────
 *
 * GIGGONE serves FOUR audiences, not one. Before this file the home page,
 * the register screen and the footer each implied a different, narrower
 * picture (mostly homeowner -> sub). Everything that renders an audience —
 * the hero selector, the role-path cards, the header nav, the footer — now
 * reads this list, so adding or renaming an audience is a one-line edit and
 * can never drift between components.
 *
 * ACCOUNT TYPES ARE UNCHANGED. There are still exactly three account roles in
 * the database (`homeowner`, `gc`, `subcontractor`). "Commercial" is not a
 * fourth account type — it is a commercial PROJECT posted by a project owner
 * account, which is why its signup link carries `type=commercial` and the
 * listing itself is stored with project_type='commercial' (the value the
 * marketplace already filters on). Nothing about auth or RLS changes here.
 */

import { Home, Building2, Hammer, HardHat } from 'lucide-react';

export type AudienceKey = 'homeowner' | 'commercial' | 'gc' | 'subcontractor';

export interface Audience {
  key: AudienceKey;
  /** lucide icon component rendered by the consuming view. */
  icon: typeof Home;
  /** i18n keys — copy lives in translations.ts, never inline. */
  titleKey: string;
  ctaKey: string;
  descKey: string;
  /** Primary conversion destination, relative (the caller localises it). */
  to: string;
  /** Secondary "learn more" landing page for this audience. */
  learn: string;
  /** Tailwind classes for the icon chip. */
  accent: string;
  /** Ring/border treatment when the card is emphasised. */
  featured?: boolean;
}

/**
 * Order is deliberate and mirrors the funnel the business wants to grow:
 * demand first (who posts the work), supply second (who buys it).
 */
export const AUDIENCES: Audience[] = [
  {
    key: 'homeowner',
    icon: Home,
    titleKey: 'aud.homeownerTitle',
    ctaKey: 'aud.homeownerCta',
    descKey: 'aud.homeownerDesc',
    to: '/register?role=homeowner',
    learn: '/homeowners',
    accent: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  {
    key: 'commercial',
    icon: Building2,
    titleKey: 'aud.commercialTitle',
    ctaKey: 'aud.commercialCta',
    descKey: 'aud.commercialDesc',
    to: '/register?role=homeowner&type=commercial',
    learn: '/commercial',
    accent: 'bg-blue-50 text-brand-blue ring-blue-100',
  },
  {
    key: 'gc',
    icon: Hammer,
    titleKey: 'aud.gcTitle',
    ctaKey: 'aud.gcCta',
    descKey: 'aud.gcDesc',
    to: '/register?role=gc',
    learn: '/general-contractors',
    accent: 'bg-amber-50 text-amber-700 ring-amber-100',
  },
  {
    key: 'subcontractor',
    icon: HardHat,
    titleKey: 'aud.subTitle',
    ctaKey: 'aud.subCta',
    descKey: 'aud.subDesc',
    to: '/jobs',
    learn: '/subcontractors',
    accent: 'bg-orange-50 text-brand-orange ring-orange-100',
    featured: true,
  },
];

/**
 * COMMERCIAL PROPERTY SEGMENTS.
 *
 * These are marketing entry points, NOT invented inventory counts. Every chip
 * links into the real marketplace filtered to commercial work
 * (`/jobs?type=commercial`), which is the same `project_type` filter the browse
 * page already supports — so a click always lands on live rows or the honest
 * empty state, never on a fabricated segment page.
 */
export interface CommercialSegment {
  key: string;
  labelKey: string;
  /** Optional trade pre-filter appended to the commercial feed. */
  trade?: string;
}

export const COMMERCIAL_SEGMENTS: CommercialSegment[] = [
  { key: 'property-managers', labelKey: 'aud.segPropertyMgr' },
  { key: 'retail', labelKey: 'aud.segRetail' },
  { key: 'restaurants', labelKey: 'aud.segRestaurants' },
  { key: 'warehouses', labelKey: 'aud.segWarehouse' },
  { key: 'office', labelKey: 'aud.segOffice' },
  { key: 'multifamily', labelKey: 'aud.segMultifamily' },
];

/** Query string for a commercial segment entry point. */
export const commercialFeedQuery = (seg?: CommercialSegment): string =>
  seg?.trade ? `?type=commercial&trade=${seg.trade}` : '?type=commercial';
